import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ShoppingCart, Plus, Minus, Trash2, ArrowRight, Package } from "lucide-react";
import { formatBHD } from "@/lib/utils";
import { products } from "@/data/products";
import { toast } from "sonner";

interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CartDrawer({ open, onOpenChange }: CartDrawerProps) {
  const { cart, cartCount, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  // Get full product details for cart items
  const cartWithDetails = cart.map((item) => {
    const product = products.find((p) => p.id === item.product_id);
    return { ...item, product };
  }).filter((item) => item.product);

  // Calculate totals
  const subtotal = cartWithDetails.reduce((sum, item) => {
    return sum + (item.product?.price || 0) * item.quantity;
  }, 0);

  const shipping = subtotal > 0 ? (subtotal >= 20 ? 0 : 2.5) : 0; // Free shipping over 20 BHD
  const total = subtotal + shipping;

  const handleIncreaseQuantity = async (productId: string, currentQuantity: number, size?: string, color?: string) => {
    try {
      await updateQuantity(productId, currentQuantity + 1, size, color);
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handleDecreaseQuantity = async (productId: string, currentQuantity: number, size?: string, color?: string) => {
    if (currentQuantity <= 1) {
      try {
        await removeFromCart(productId, size, color);
        toast.success("Item removed from cart");
      } catch (error) {
        toast.error("Failed to remove item");
      }
    } else {
      try {
        await updateQuantity(productId, currentQuantity - 1, size, color);
      } catch (error) {
        toast.error("Failed to update quantity");
      }
    }
  };

  const handleRemoveItem = async (productId: string, size?: string, color?: string) => {
    try {
      await removeFromCart(productId, size, color);
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleCheckout = () => {
    onOpenChange(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isRTL ? "left" : "right"} className="w-full sm:max-w-lg flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Shopping Cart
            {cartCount > 0 && (
              <span className="text-sm text-muted-foreground">({cartCount} items)</span>
            )}
          </SheetTitle>
          <SheetDescription>
            {cartCount === 0 ? "Your cart is empty" : "Review your items before checkout"}
          </SheetDescription>
        </SheetHeader>

        {cartCount === 0 ? (
          // Empty State
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-muted p-6 mb-4">
              <ShoppingCart className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
            <p className="text-sm text-muted-foreground mb-6 text-center">
              Start shopping to add items to your cart
            </p>
            <Button onClick={() => { onOpenChange(false); navigate("/shop"); }}>
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cartWithDetails.map((item) => (
                  <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4 group">
                    {/* Product Image */}
                    <div className="relative h-20 w-20 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.product?.image}
                        alt={item.product?.name}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm leading-tight line-clamp-2">
                            {item.product?.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            {item.size && <span className="px-1.5 py-0.5 bg-muted rounded">{item.size}</span>}
                            {item.color && (
                              <span className="px-1.5 py-0.5 bg-muted rounded flex items-center gap-1">
                                <span
                                  className="w-3 h-3 rounded-full border"
                                  style={{ backgroundColor: item.color }}
                                />
                                {item.color}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Remove Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={() => handleRemoveItem(item.product_id, item.size, item.color)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Price & Quantity */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-sm font-bold text-primary">
                          {formatBHD((item.product?.price || 0) * item.quantity)}
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2 border rounded-md">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDecreaseQuantity(item.product_id, item.quantity, item.size, item.color)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleIncreaseQuantity(item.product_id, item.quantity, item.size, item.color)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Cart Summary */}
            <div className="border-t pt-4 space-y-4">
              {/* Free Shipping Progress */}
              {shipping > 0 && subtotal < 20 && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <Package className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">
                      Add {formatBHD(20 - subtotal)} more for free shipping!
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                      className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min((subtotal / 20) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatBHD(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">
                    {shipping === 0 ? <span className="text-green-600">FREE</span> : formatBHD(shipping)}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">{formatBHD(total)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  className="w-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white"
                  size="lg"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout
                  <ArrowRight className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { onOpenChange(false); navigate("/shop"); }}
                >
                  Continue Shopping
                </Button>
              </div>

              {/* Clear Cart */}
              {cartCount > 0 && (
                <Button
                  variant="link"
                  className="w-full text-destructive"
                  onClick={async () => {
                    try {
                      await clearCart();
                      toast.success("Cart cleared");
                    } catch (error) {
                      toast.error("Failed to clear cart");
                    }
                  }}
                >
                  Clear Cart
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}